import { RPCEmitterContext, RPCEventEmitter } from "@bloomstack/greenhouse/modules/rpc/types/rpcTypes";
import { useSocketIOContext } from "@/contexts/socketIo"
import { useGroupSubscriptionContextOptional } from "@/contexts/groupSubscription";
import type { IService } from "@bloomstack/greenhouse/types";
import { DependencyList, useCallback, useEffect, useMemo } from "react";
import { useUser } from "./useUser";

// ─── Priority / pool types ────────────────────────────────────────

/** Which concurrency pool to route an RPC call through. */
export type RpcPool = "high" | "normal";

/** Dynamic ordering function evaluated at drain time. Higher values drain first. */
export type RpcOrderFn = () => number;

// ─── Internal limiter types ───────────────────────────────────────

type QueueEntry = { resolve: () => void; orderFn?: RpcOrderFn };

type Pool = { maxConcurrent: number; inFlight: number; queue: QueueEntry[] };

// ─── RpcLimiter ───────────────────────────────────────────────────

/**
 * Dual-pool concurrency limiter. Two independent pools (high / normal) each with
 * their own concurrency limit and queue. Queued entries are drained by highest
 * orderFn() value, with FIFO as tiebreaker for equal values.
 */
export class RpcLimiter {
  private readonly pools: Record<RpcPool, Pool>;

  constructor(limits: { high: number; normal: number }) {
    this.pools = {
      high: { maxConcurrent: limits.high, inFlight: 0, queue: [] },
      normal: { maxConcurrent: limits.normal, inFlight: 0, queue: [] },
    };
  }

  /** Acquire a slot in the given pool. Resolves immediately if a slot is free; queues otherwise. */
  acquire(pool: RpcPool, orderFn?: RpcOrderFn, method?: string): Promise<void> {
    const p = this.pools[pool];
    if (p.inFlight < p.maxConcurrent) {
      p.inFlight++;
      return Promise.resolve();
    }
    return new Promise<void>(resolve => {
      p.queue.push({ resolve, orderFn });
    });
  }

  /** Release a slot in the given pool. If the queue has entries, drains the best one. */
  release(pool: RpcPool, method?: string): void {
    const p = this.pools[pool];
    p.inFlight--;
    if (p.queue.length === 0) return;

    // Pick the entry with the highest orderFn value; FIFO as tiebreaker for equal values.
    let bestIdx = 0;
    let bestValue = p.queue[0].orderFn?.() ?? 0;
    for (let i = 1; i < p.queue.length; i++) {
      const val = p.queue[i].orderFn?.() ?? 0;
      if (val > bestValue) {
        bestValue = val;
        bestIdx = i;
      }
    }

    const [entry] = p.queue.splice(bestIdx, 1);
    p.inFlight++;
    entry.resolve();
  }
}

/** Shared singleton used by all useService proxies in this browser tab. */
const rpcLimiter = new RpcLimiter({ high: 5, normal: 2 });

// ─── ServiceOptions ───────────────────────────────────────────────

/**
 * Options controlling RPC call behavior.
 */
type ServiceOptions = {
  timeout: number;
  retries: number;
  pool: RpcPool;
  orderFn?: RpcOrderFn;
};

const DEFAULT_OPTIONS: ServiceOptions = { timeout: 10000, retries: 0, pool: "normal" };

const RETRYABLE_PATTERNS = ["timed out", "Socket not connected", "transport close"];

function isRetryableError(err: unknown): boolean {
  const msg = err instanceof Error ? err.message : String(err);
  return RETRYABLE_PATTERNS.some(pattern => msg.includes(pattern));
}

/**
 * Type wrapper that adds withTimeout, withRetry, and withPriority methods to every
 * method of the service.
 */
export type ServiceWrapper<T extends IService> = T & {
  withTimeout: (timeout: number) => ServiceWrapper<T>;
  withRetry: (retries: number) => ServiceWrapper<T>;
  withPriority: (pool: RpcPool, orderFn?: RpcOrderFn) => ServiceWrapper<T>;
}

/**
 * Creates a proxy that routes method calls through methodCall with the given options.
 * withTimeout, withRetry, and withPriority return new proxies with merged options,
 * enabling composability. All method calls are gated through the provided RpcLimiter.
 *
 * Exported for testing — production code uses the module-level rpcLimiter singleton.
 */
export function createServiceProxy<T extends IService>(
  methodCall: (timeout: number, method: string, args: any[]) => Promise<any>,
  options: ServiceOptions,
  limiter: RpcLimiter = rpcLimiter,
): ServiceWrapper<T> {
  // Cache of child proxies keyed by serializable option values.
  // Ensures withTimeout/withRetry/withPriority return the same reference
  // across React re-renders when called with the same arguments.
  const childCache = new Map<string, ServiceWrapper<T>>();

  function getOrCreate(key: string, newOptions: ServiceOptions): ServiceWrapper<T> {
    let child = childCache.get(key);
    if (!child) {
      child = createServiceProxy<T>(methodCall, newOptions, limiter);
      childCache.set(key, child);
    } else {
      // Update mutable fields (orderFn is a new closure each render
      // but evaluated lazily at drain time — safe to replace)
      newOptions.orderFn !== undefined && ((child as any).__options.orderFn = newOptions.orderFn);
    }
    return child;
  }

  const callWithRetry = async (method: string, args: any[]): Promise<any> => {
    const { timeout, retries } = options;
    let lastError: unknown;

    for (let attempt = 0; attempt <= retries; attempt++) {
      try {
        return await methodCall(timeout, method, args);
      } catch (err) {
        lastError = err;
        if (attempt < retries && isRetryableError(err)) {
          const delay = Math.min(1000 * Math.pow(2, attempt), 5000);
          await new Promise(r => setTimeout(r, delay));
          continue;
        }
        throw err;
      }
    }

    throw lastError;
  };

  const proxy = new Proxy({} as ServiceWrapper<T>, {
    get(_, prop) {
      if (typeof prop !== "string") return undefined;

      // Expose options for parent cache to update mutable fields (orderFn)
      if (prop === "__options") return options;

      if (prop === "withTimeout") {
        return (timeout: number) =>
          getOrCreate(`t:${timeout}`, { ...options, timeout });
      }
      if (prop === "withRetry") {
        return (retries: number) =>
          getOrCreate(`r:${retries}`, { ...options, retries });
      }
      if (prop === "withPriority") {
        return (pool: RpcPool, orderFn?: RpcOrderFn) =>
          getOrCreate(`p:${pool}`, { ...options, pool, orderFn });
      }

      if (options.retries > 0 || options.retries === Infinity) {
        return async (...args: any[]) => {
          await limiter.acquire(options.pool, options.orderFn, prop);
          try {
            return await callWithRetry(prop, args);
          } finally {
            limiter.release(options.pool, prop);
          }
        };
      }
      return async (...args: any[]) => {
        await limiter.acquire(options.pool, options.orderFn, prop);
        try {
          return await methodCall(options.timeout, prop, args);
        } finally {
          limiter.release(options.pool, prop);
        }
      };
    },
  });

  return proxy;
}

/**
 * A hook that uses socket.io to connect to a service and return the service object.
 * @param ident 
 */
export const useService = <T extends IService>(ident: string): ServiceWrapper<T> => {
  const socketio = useSocketIOContext();

  const service = useMemo(() => {
    const methodCall = (timeout: number, method: string, args: any[]) => {
      if (!method || typeof method !== "string") {
        console.warn(`[useService] Invalid RPC method name for ${ident}:`, method, new Error().stack);
        return Promise.reject(new Error(`Invalid RPC method name: ${method}`));
      }
      if (!socketio.socket) {
        return Promise.reject(new Error("Socket not connected"));
      }
      return new Promise((resolve, reject) => {
        const callback = (err: any, result: any) => {
          if (err) {
            reject(err);
            return;
          }
          
          resolve(result);
        };

        try {
          socketio.socket!.timeout(timeout).emit("rpc_invoke", ident, method, args, callback);
        } catch (e) {
          reject(e);
        }
      });
    };

    return createServiceProxy<T>(methodCall, DEFAULT_OPTIONS);
  }, [ident, socketio.socket]);

  return service;
}

type OmitContext<T> = T extends (context: RPCEmitterContext, ...restArgs: infer P) => void ? P : never
export const useServiceEventListener = <T extends (...args: any[]) => void >(
  ident: string,
  callback: (...args: OmitContext<T>) => void,
  deps?: DependencyList
) => {
  const socketio = useSocketIOContext();

  const listener = useCallback((eventIdent: string, ...eventArgs: OmitContext<T>) => {
    if (eventIdent !== ident) {
      return;
    }

    return callback(...eventArgs);
  }, [deps]);

  // we'll register to two rooms:
  // 1. rpc_emit:<ident>:<to> for one user
  // 2. rpc_emit:<ident>:* for all users
  useEffect(() => {
    if (!socketio.socket || !socketio.isConnected) {
      return;
    }

    socketio.socket.on(`rpc_emit`, listener as any);

    return () => {
      socketio.socket?.off(`rpc_emit`, listener as any);
    };
  }, [ident, listener, socketio.socket, socketio.isConnected]);
};

/**
 * Subscribe to a group for RPC events.
 * 
 * This hook automatically handles:
 * - Permission validation (server-side)
 * - Request deduplication
 * - Re-subscription on socket reconnect
 * 
 * @param type - The group type ("workspace", "org")
 * @param id - The group ID (workspace ID, org ID) or null to skip subscription
 * 
 * @example
 * ```tsx
 * // Subscribe to workspace events
 * useServiceGroup("workspace", workspaceId);
 * 
 * // Event listeners work normally - subscription is transparent
 * useServiceEventListener<FileDeletedEvent>(RPC_EVENT_FILE_DELETED_IDENT, ...);
 * ```
 */
export const useServiceGroup = (type: string, id: string | null): void => {
  const groupCtx = useGroupSubscriptionContextOptional();

  useEffect(() => {
    if (!id || !groupCtx) {
      return;
    }

    groupCtx.requestSubscription(type, id);
  }, [type, id, groupCtx]);
};
